// "Knowledge base changes": the changes that touch the help register
// (Scout Documentation, Help & OTA Register v4), each with the Doc IDs its
// commit names ("Help-guide: HOME-ALARM-001"), or "new HOME" for something
// that needs a row. In their own panel at the top of an update or a day,
// folded until opened. Each change keeps its commit's own words.

import { createContext, useContext, useState, type ReactNode } from 'react';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import { cx } from '../../../../components/admin/console';
import { AuthorMark, Chevron, CommitChip, CopyButton, PrChip } from './parts';
import type { KbChange, RegisterRow, SectionKind } from '../data/changelog';
import type { CopyFormat } from './UpdateCard';

/** The register's rows by Doc ID, for the chips' "what and where"; null when the copy couldn't be read. */
export const RegisterContext = createContext<Record<string, RegisterRow> | null>(null);

/** Lines shown per app or part before "Show all". */
const PREVIEW = 5;

const TAG: Record<SectionKind, { word: string; ink: string }> = {
  breaking: { word: 'Changed', ink: 'text-red-300' },
  features: { word: 'New', ink: 'text-cyan-300' },
  fixes: { word: 'Fixed', ink: 'text-emerald-300' },
  speedups: { word: 'Faster', ink: 'text-violet-300' },
  reverts: { word: 'Undone', ink: 'text-slate-300' },
  other: { word: 'Changed', ink: 'text-slate-300' },
};
const ORDER: SectionKind[] = ['breaking', 'features', 'fixes', 'speedups', 'reverts', 'other'];

/** One commit's change: its words, the Doc IDs it names ("new <MODULE>" for no row yet), and who and where. */
export type KbItem = KbChange;

export interface KbGroup {
  id: string;
  name: string;
  icon: ReactNode;
  items: KbItem[];
}

const sorted = (items: KbItem[]) => [...items].sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind));
const isNewRow = (doc: string) => doc.startsWith('new ');
/** "new NAVIGATION" → "New row · Navigation" */
const newRowLabel = (doc: string) => `New row · ${doc.slice(4).charAt(0)}${doc.slice(5).toLowerCase().replace(/-/g, ' ')}`;

export function kbText(groups: KbGroup[], heading: string, md: boolean): string {
  const out = [md ? `## ${heading}` : heading];
  for (const g of groups) {
    out.push('', md ? `### ${g.name}` : g.name);
    for (const it of sorted(g.items)) out.push(`- ${TAG[it.kind].word}: ${it.text} [${it.docs.join(', ')}]`);
  }
  return out.join('\n');
}

export function KbChanges({ groups, id, heading, format, quietWhenEmpty = true }: {
  groups: KbGroup[];
  id: string;
  /** What the copied text is headed with: "Knowledge base changes, Thu 15 Oct". */
  heading: string;
  format: CopyFormat;
  /** Say so when nothing touches the register; off where the register has no rows yet. */
  quietWhenEmpty?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const shown = groups.filter((g) => g.items.length);
  const total = shown.reduce((n, g) => n + g.items.length, 0);
  const docs = new Set(shown.flatMap((g) => g.items.flatMap((it) => it.docs)));
  const rows = [...docs].filter((d) => !isNewRow(d)).length;
  const newRows = shown.reduce((n, g) => n + g.items.filter((it) => it.docs.some(isNewRow)).length, 0);
  if (!total) {
    if (!quietWhenEmpty) return null;
    return (
      <div className="flex items-center gap-3 rounded-xl border border-slate-700/50 px-3 py-2.5 sm:px-3.5">
        <BookOpenIcon className="h-[18px] w-[18px] flex-none text-slate-500" aria-hidden="true" />
        <span className="text-[15px] text-slate-400">No knowledge base changes</span>
      </div>
    );
  }
  return (
    <section
      aria-labelledby={`${id}-h`}
      className="overflow-hidden rounded-xl border border-indigo-300/20 bg-gradient-to-br from-indigo-400/[0.09] via-slate-900/40 to-cyan-400/[0.05]"
    >
      <div className="flex items-center gap-1 px-1.5 py-1.5 transition-colors hover:bg-indigo-300/[0.05] sm:px-2.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={`${id}-body`}
          className="flex min-h-[48px] min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          <Chevron open={open} className="h-[18px] w-[18px]" />
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[9px] bg-gradient-to-br from-cyan-400/25 to-indigo-400/30 ring-1 ring-inset ring-indigo-200/20" aria-hidden="true">
            <BookOpenIcon className="h-5 w-5 text-indigo-100" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span id={`${id}-h`} className="text-base font-semibold text-white">Knowledge base changes</span>
            <span className="text-sm text-slate-400">
              {total} {total === 1 ? 'change' : 'changes'} · {rows} register {rows === 1 ? 'row' : 'rows'}
              {newRows > 0 && <> · {newRows} {newRows === 1 ? 'needs a new row' : 'need new rows'}</>}
            </span>
          </span>
        </button>
        <CopyButton iconOnly getText={() => kbText(shown, heading, format === 'markdown')} ariaLabel={`Copy ${heading}`} />
      </div>
      {open && (
        <div id={`${id}-body`} className="space-y-5 border-t border-indigo-300/15 px-3 pb-4 pt-3.5 sm:pl-[60px] sm:pr-5">
          {shown.map((g) => <Group key={g.id} group={g} />)}
        </div>
      )}
    </section>
  );
}

function Group({ group: g }: { group: KbGroup }) {
  const [all, setAll] = useState(false);
  const items = sorted(g.items);
  const visible = all ? items : items.slice(0, PREVIEW);
  return (
    <div>
      <div className="flex items-center gap-2.5">
        {g.icon}
        <span className="text-[15px] font-semibold text-white">{g.name}</span>
        <span className="text-sm text-slate-500">{items.length}</span>
      </div>
      <ul className="mt-2 space-y-2.5">
        {visible.map((it) => (
          <li key={it.key} className="flex items-baseline gap-3 text-[15px] leading-snug text-slate-200">
            {/* Phones put the tag before the words, so the Doc IDs keep the width. */}
            <span className={cx('hidden w-[52px] flex-none text-[13px] font-semibold sm:block', TAG[it.kind].ink)}>{TAG[it.kind].word}</span>
            <span className="min-w-0 flex-1">
              <span className={cx('mr-2 text-[13px] font-semibold sm:hidden', TAG[it.kind].ink)}>{TAG[it.kind].word}</span>
              {it.text}
              <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {it.docs.map((d) => <DocChip key={d} doc={d} />)}
                <span className="ml-auto pl-1 sm:hidden"><Who item={it} /></span>
              </span>
            </span>
            <span className="hidden self-start sm:block"><Who item={it} /></span>
          </li>
        ))}
      </ul>
      {items.length > PREVIEW && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          className="mt-2.5 inline-flex min-h-[36px] items-center rounded-lg border border-indigo-300/20 px-3 text-sm font-medium text-slate-300 hover:bg-indigo-300/[0.07] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          {all ? 'Show fewer' : `Show all ${items.length}`}
        </button>
      )}
    </div>
  );
}

/** Who made it, its PR and its commit, as on the change lists below. */
function Who({ item: it }: { item: KbItem }) {
  return (
    <span className="flex flex-none items-center gap-1.5">
      {it.author && <AuthorMark author={it.author} />}
      {it.pr && <PrChip number={it.pr.number} url={it.pr.url} />}
      {it.sha && it.url && <CommitChip sha={it.sha} url={it.url} />}
    </span>
  );
}

/** A Doc ID; its element and where it is on hover or focus. "new X" is a row still to write. */
function DocChip({ doc }: { doc: string }) {
  const register = useContext(RegisterContext);
  const row = register?.[doc];
  const fresh = isNewRow(doc);
  const label = fresh ? newRowLabel(doc) : doc;
  // An ID the copy lacks is only flagged when the copy was read: CI checks new ones.
  const unknown = !fresh && !row && register !== null;
  const tip = fresh
    ? 'Nothing in the register yet: this needs a row.'
    : row
      ? row.retired ? `${row.path}. Retired: ${row.retired}` : row.path
      : unknown ? 'Not in the register: check this Doc ID.' : "The register couldn't be read just now.";
  return (
    <span className="group/doc relative">
      <span
        tabIndex={0}
        aria-label={row ? `${doc}, ${row.element}. ${tip}` : `${label}. ${tip}`}
        className={cx(
          'inline-flex cursor-default items-center whitespace-nowrap rounded-md border px-2 py-0.5 font-mono text-[13px] leading-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500',
          fresh ? 'border-dashed border-amber-300/40 font-sans font-medium text-amber-200' : unknown ? 'border-red-400/40 text-red-300' : 'border-indigo-300/25 bg-indigo-300/[0.07] text-indigo-100',
        )}
      >
        {label}
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 w-max max-w-[320px] rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-left opacity-0 shadow-xl shadow-black/40 transition-opacity duration-100 group-hover/doc:opacity-100 group-focus-within/doc:opacity-100"
      >
        {row && <span className="block text-[13px] font-semibold text-white">{row.element}</span>}
        <span className="block text-[13px] leading-snug text-slate-400">{tip}</span>
      </span>
    </span>
  );
}
